/**
 * The language registry — ONE list, shared by the API and the web app.
 * @file src/shared/languages.ts
 *
 * ── Why this file exists ──────────────────────────────────────────────────
 * "Adding a language is a data change" was true for the firm's half of the
 * product (their questions and copy are per-language maps in the database)
 * and false for ours: the list of codes lived in eight places and the chrome
 * a client reads ("Continue", "Saved", the invitation email) lived in five
 * more. Adding Portuguese meant editing thirteen files and missing one.
 *
 * Now there is this list, and the chrome bundles typed against it:
 *   src/web/shared/i18n.ts                       — on screen (form, signing)
 *   src/api/features/form/invite.service.ts      — the invitation email
 *   src/api/features/signature/signature.service — the signature email
 *
 * ── The one rule ──────────────────────────────────────────────────────────
 * `Language` is the union of codes whose chrome is COMPLETE. Every bundle is
 * a `Record<Language, …>`, so adding a code to LANGUAGES makes TypeScript
 * list every bundle that still lacks it. A language cannot be half-shipped
 * by accident — which matters because a half-shipped language is exactly the
 * competitor failure this product exists to avoid: their buttons in English
 * around the firm's translated questions.
 *
 * ── Thirty-three languages, and what "quality" means ──────────────────────
 * Two markets set the list. For the US: the languages most spoken by
 * limited-English-proficiency clients (Spanish, Chinese, Vietnamese, Korean,
 * Tagalog, Arabic, Haitian Creole, Russian, Portuguese, French, and the South
 * Asian languages). For the Middle East: Arabic first, then the Gulf's
 * expatriate workforce — Hindi, Urdu, Malayalam, Tamil, Bengali, Nepali,
 * Tagalog — plus Persian, Turkish, Hebrew, Armenian and Pashto.
 *
 * Deliberately NOT here: Punjabi, Gujarati, Telugu and Sinhala. Each has
 * speakers in both markets, but they skew toward populations that are
 * either English-comfortable or served by a language already on the list,
 * so they were not worth thirty more strings to keep true. Kurdish
 * (Sorani), Dari and Egyptian Arabic are the named next candidates;
 * Persian and Modern Standard Arabic cover the last two in practice.
 *
 * The launch three (en, es, ru) were reviewed by native speakers. The rest
 * were drafted by us and are marked `draft`: correct, plain, and worth a
 * native speaker's ten minutes before a firm relies on them. The settings
 * page shows the mark; flipping it is a one-word change here once the
 * review has happened.
 *
 * ── No imports, on purpose ────────────────────────────────────────────────
 * This file is loaded by the Vite bundle and by the Node API. It must stay
 * free of Node, DOM and package imports so both can take it as-is.
 *
 * @llm-rule WHEN: Anything needs the list of languages, a language's name,
 *   or its text direction
 * @llm-rule AVOID: A local `LANGUAGES` array anywhere else — import this
 * @llm-rule AVOID: Adding a code to LANGUAGES without filling every bundle;
 *   the compiler will tell you which ones
 * @llm-rule AVOID: Marking a language `reviewed` without a native review
 */

/** Codes with complete chrome — the only ones a firm can switch on. */
export const LANGUAGES = [
  'en', 'es', 'zh-Hans', 'zh-Hant', 'vi', 'ko', 'tl', 'ar', 'ht', 'pt',
  'fr', 'ru', 'uk', 'pl', 'hi', 'bn', 'ur', 'fa',
  'ja', 'de', 'it', 'he', 'am', 'so', 'tr', 'th', 'km', 'sw',
  'ml', 'ta', 'ne', 'hy', 'ps',
] as const;
export type Language = (typeof LANGUAGES)[number];

/**
 * The base language. Every form carries it, every localised map falls back
 * to it, and `resolveLabel` reports `missing` when it had to. Not a
 * preference — the anchor the coverage arithmetic hangs off.
 */
export const BASE_LANGUAGE: Language = 'en';

/**
 * What a firm serves until it says otherwise. The launch set, not all
 * thirty-three: "serves" is a claim about having a translator, and a firm should
 * make it, not inherit it. Matches the column default in schema.prisma.
 */
export const DEFAULT_LANGUAGES: readonly Language[] = ['en', 'es', 'ru'];

export type TextDirection = 'ltr' | 'rtl';

export interface LanguageInfo {
  /** BCP 47 tag, as stored on leads, forms and submissions. */
  code: string;
  /** English name, for staff-facing lists. */
  name: string;
  /** The name in the language itself, for the client-facing switcher. */
  native: string;
  dir: TextDirection;
  /**
   * `complete` — every chrome bundle has it; a firm may enable it.
   * `planned`  — listed so a firm can see and ask; not selectable.
   */
  chrome: 'complete' | 'planned';
  /**
   * `reviewed` — a native speaker has read our chrome in this language.
   * `draft`    — written by us, not yet reviewed. Selectable, and marked.
   */
  quality: 'reviewed' | 'draft';
}

/*
 * Order: roughly by US limited-English-proficiency population after
 * English, with the launch three kept near the top. Right-to-left entries
 * carry `dir: 'rtl'`, which the public form and signing page apply to the
 * document — the bundles exist, the mirrored layout is verified per screen.
 */
export const REGISTRY: readonly LanguageInfo[] = [
  { code: 'en', name: 'English', native: 'English', dir: 'ltr', chrome: 'complete', quality: 'reviewed' },
  { code: 'es', name: 'Spanish', native: 'Español', dir: 'ltr', chrome: 'complete', quality: 'reviewed' },
  { code: 'ru', name: 'Russian', native: 'Русский', dir: 'ltr', chrome: 'complete', quality: 'reviewed' },
  { code: 'zh-Hans', name: 'Chinese (Simplified)', native: '简体中文', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'zh-Hant', name: 'Chinese (Traditional)', native: '繁體中文', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'vi', name: 'Vietnamese', native: 'Tiếng Việt', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'ko', name: 'Korean', native: '한국어', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'tl', name: 'Tagalog', native: 'Tagalog', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'ar', name: 'Arabic', native: 'العربية', dir: 'rtl', chrome: 'complete', quality: 'draft' },
  { code: 'ht', name: 'Haitian Creole', native: 'Kreyòl ayisyen', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'pt', name: 'Portuguese', native: 'Português', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'fr', name: 'French', native: 'Français', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'uk', name: 'Ukrainian', native: 'Українська', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'pl', name: 'Polish', native: 'Polski', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'hi', name: 'Hindi', native: 'हिन्दी', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'bn', name: 'Bengali', native: 'বাংলা', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'ur', name: 'Urdu', native: 'اردو', dir: 'rtl', chrome: 'complete', quality: 'draft' },
  { code: 'fa', name: 'Persian', native: 'فارسی', dir: 'rtl', chrome: 'complete', quality: 'draft' },
  { code: 'ja', name: 'Japanese', native: '日本語', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'de', name: 'German', native: 'Deutsch', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'it', name: 'Italian', native: 'Italiano', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'he', name: 'Hebrew', native: 'עברית', dir: 'rtl', chrome: 'complete', quality: 'draft' },
  { code: 'am', name: 'Amharic', native: 'አማርኛ', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'so', name: 'Somali', native: 'Soomaali', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'tr', name: 'Turkish', native: 'Türkçe', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'th', name: 'Thai', native: 'ไทย', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'km', name: 'Khmer', native: 'ខ្មែរ', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'sw', name: 'Swahili', native: 'Kiswahili', dir: 'ltr', chrome: 'complete', quality: 'draft' },

  /* The Gulf's expatriate workforce, and two diaspora languages that matter
   * in both markets (Armenian in Lebanon and Los Angeles; Pashto for Afghan
   * communities in the US and the Gulf). */
  { code: 'ml', name: 'Malayalam', native: 'മലയാളം', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'ta', name: 'Tamil', native: 'தமிழ்', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'ne', name: 'Nepali', native: 'नेपाली', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'hy', name: 'Armenian', native: 'Հայերեն', dir: 'ltr', chrome: 'complete', quality: 'draft' },
  { code: 'ps', name: 'Pashto', native: 'پښتو', dir: 'rtl', chrome: 'complete', quality: 'draft' },
];

export function isLanguage(v: unknown): v is Language {
  return typeof v === 'string' && (LANGUAGES as readonly string[]).includes(v);
}

export function languageInfo(code: string): LanguageInfo | undefined {
  return REGISTRY.find((l) => l.code === code);
}

/**
 * The name to show for a code. Native by default — a client choosing their
 * language should find it written the way they would write it. Falls back to
 * the code itself rather than blank, so an unexpected value is visible.
 */
export function languageName(code: string, form: 'native' | 'english' = 'native'): string {
  const info = languageInfo(code);
  if (!info) return code;
  return form === 'english' ? info.name : info.native;
}

/**
 * A language's name written IN another language — "inglés", not "English",
 * inside a Spanish sentence.
 *
 * `languageName` gives the endonym, which is right for a switcher (you look
 * for your language written the way you write it) and wrong inside prose
 * ("Este acuerdo está redactado en English"). Intl.DisplayNames has this
 * data in every modern browser and in Node, so we do not carry 33×33 names.
 *
 * Falls back to the endonym if the runtime has no data for the pair — a
 * slightly foreign-looking name beats a blank in a legal sentence.
 */
export function languageNameIn(code: string, readerLocale: string): string {
  try {
    const dn = new Intl.DisplayNames([readerLocale], { type: 'language' });
    return dn.of(code) ?? languageName(code);
  } catch {
    return languageName(code);
  }
}

export function languageDir(code: string): TextDirection {
  return languageInfo(code)?.dir ?? 'ltr';
}

/** The registry entries a firm may actually switch on. */
export function selectableLanguages(): LanguageInfo[] {
  return REGISTRY.filter((l) => l.chrome === 'complete');
}
